import { useState, useEffect, useCallback } from 'react';
import { Exercise } from '@/models/LearnTypes';
import { Lesson } from '@/models/Lesson';
import { LEVEL_CONFIGS } from '@/constants/levelConfigs';
import { useLevelProgression } from '@/hooks/useLevelProgression';
import { useExerciseState } from '@/hooks/useExerciseState';
import { useLevelManagement } from '@/hooks/useLevelManagement';
import { UseLevelStateProps, UseLevelStateReturn } from './useLevelState.types';
import { generateExercisesForLevel } from '@/utils/exerciseUtils';
import { calculateLessonBonus } from './progression/useXPCalculation';
import { useWordRecycling } from './level/useWordRecycling';
import { usePerfectLevel } from '@/contexts/PerfectLevelContext';

export const useLevelState = ({ userId }: UseLevelStateProps): UseLevelStateReturn => {
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [totalXpGained, setTotalXpGained] = useState(0);
  
  const [isPerfectLesson, setIsPerfectLesson] = useState<boolean>(true);
  const [lessonBonus, setLessonBonus] = useState<number>(0);
  
  const { allLevelsPerfect, markLevelImperfect, resetLevelPerfectStatus } = usePerfectLevel();

  const [debugInfo, setDebugInfo] = useState({
    lastAction: '',
    recyclingStatus: false,
    failedCards: [] as string[]
  });

  const updateDebugInfo = useCallback((info: Partial<typeof debugInfo>) => {
    setDebugInfo(prev => {
      const updated = { ...prev, ...info };
      console.log(`🐛 [useLevelState] Debug info updated:`, updated);
      return updated;
    });
  }, []);
  
  const {
    currentExerciseIndex,
    showAnswer,
    userInput,
    isInputCorrect,
    wordsToReview,
    totalOriginalExercises,
    correctlyAnsweredWords,
    displayedExerciseNumber,
    
    setCurrentExerciseIndex,
    setShowAnswer,
    setUserInput,
    setIsInputCorrect,
    setWordsToReview,
    setTotalOriginalExercises,
    
    handleCheckAnswer: baseHandleCheckAnswer,
    handleNextExercise: baseHandleNextExercise,
    handlePrevExercise,
    
    currentExercise,
    progress,
    isLastExercise,
    totalExercises,
    
    onLogActivity
  } = useExerciseState({
    exercises,
    setExercises,
    userId,
    selectedLesson
  });
  
  const { resetRecyclingCounts } = useWordRecycling({
    wordsToReview,
    setWordsToReview,
    setIsPerfectLevel: () => {}, // Placeholder, will be updated
    setShowLevelComplete: () => {}, // Placeholder, will be updated
    currentLevelId: selectedLesson?.id
  });
  
  const {
    currentLevelIndex,
    levels,
    showLevelIntro,
    showLevelComplete,
    showLessonComplete,
    
    setCurrentLevelIndex,
    setLevels,
    setShowLevelIntro,
    setShowLevelComplete,
    setShowLessonComplete,
    
    startLevel,
    
    currentLevel
  } = useLevelManagement({
    setExercises,
    setTotalOriginalExercises,
    setCurrentExerciseIndex,
    setShowAnswer,
    setUserInput,
    setIsInputCorrect,
    setWordsToReview,
    selectedLesson
  });
  
  const handleImperfectLesson = useCallback(() => {
    console.debug(`🏆 [handleImperfectLesson] Setting lesson as imperfect`);
    setIsPerfectLesson(false);
    setLessonBonus(0);
    
    if (currentLevelIndex !== undefined && currentLevelIndex >= 0) {
      console.log(`❌ [handleImperfectLesson] Marking level ${currentLevelIndex} as imperfect in context`);
      markLevelImperfect(currentLevelIndex);
    }
    
    (window as any).__PERFECT_LESSON__ = false;
    
    if (selectedLesson?.id) {
      try {
        localStorage.setItem(`lesson_${selectedLesson.id}_perfect`, 'false');
        console.debug(`🔐 Saved imperfect lesson status to localStorage for lesson ${selectedLesson.id}`);
      } catch (e) {
        console.error('Error setting localStorage:', e);
      }
    }
  }, [selectedLesson, currentLevelIndex, markLevelImperfect]);
  
  const { 
    completeLevel, 
    moveToNextLevel,
    skipToNextLevel,
    completeLessonAndAwardXP,
    isPerfectSpelling,
    isPerfectLevel,
    previouslyCompletedLevels,
    checkPreviouslyCompletedLevels,
    levelCompletionCounts,
    restartCurrentLevel,
    practiceMode: isPracticeMode,
    resetLessonCheck
  } = useLevelProgression({
    userId,
    levels,
    setLevels,
    currentLevelIndex,
    setCurrentLevelIndex,
    totalXpGained,
    setTotalXpGained,
    selectedLesson,
    setShowLevelComplete,
    setShowLessonComplete,
    setShowLevelIntro,
    setExercises,
    currentExerciseIndex,
    setCurrentExerciseIndex,
    setShowAnswer,
    setUserInput,
    setIsInputCorrect,
    setWordsToReview,
    setTotalOriginalExercises,
    handleImperfectLesson
  });
  
  const handleCheckAnswer = useCallback(() => {
    updateDebugInfo({ lastAction: 'check-answer' });
    baseHandleCheckAnswer();
  }, [baseHandleCheckAnswer, updateDebugInfo]);
  
  const handleNextExercise = useCallback(() => {
    updateDebugInfo({ 
      lastAction: 'next-exercise', 
      failedCards: [...wordsToReview]
    });
    
    console.log(`🚀 [handleNextExercise] About to process next exercise`);
    console.log(`🚀 [handleNextExercise] Current wordsToReview:`, wordsToReview);
    console.log(`🚀 [handleNextExercise] Current exercises count: ${exercises.length}, original: ${totalOriginalExercises}`);
    
    baseHandleNextExercise((failedWords) => {
      console.log(`🚀 [handleNextExercise] Level completion callback with failedWords:`, failedWords);
      console.log(`🚀 [handleNextExercise] Original wordsToReview:`, wordsToReview);
      
      updateDebugInfo({ 
        recyclingStatus: failedWords.length > 0,
        failedCards: [...failedWords]
      });
      
      completeLevel(failedWords);
    });
  }, [
    baseHandleNextExercise, 
    completeLevel, 
    exercises.length, 
    totalOriginalExercises, 
    updateDebugInfo, 
    wordsToReview
  ]);
  
  useEffect(() => {
    console.log(`👁️ [useLevelState] wordsToReview updated:`, wordsToReview);
    updateDebugInfo({ failedCards: [...wordsToReview] });
  }, [wordsToReview, updateDebugInfo]);
  
  const checkAllLevelsPerfect = () => {
    if (!selectedLesson) return false;
    
    let allPerfect = true;
    let completedLevelCount = 0;
    
    for (let i = 0; i < levels.length; i++) {
      if (levels[i].state !== 'completed') continue;
      
      completedLevelCount++;
      
      const levelPerfect = levels[i].isPerfect;
      const levelPerfectFromWindow = (window as any)[`level_${i}_perfect_status`] === true;
      
      let levelPerfectFromStorage = false;
      try {
        levelPerfectFromStorage = localStorage.getItem(`level_${i}_perfect_status`) === 'true';
      } catch (e) {
        console.error(`Error reading level ${i} perfect status from localStorage:`, e);
      }
      
      console.debug(`🏆 [checkAllLevelsPerfect] Level ${i} perfect status: level=${levelPerfect}, window=${levelPerfectFromWindow}, storage=${levelPerfectFromStorage}`);
      
      const combinedPerfectStatus = levelPerfect !== false && 
                                   (levelPerfectFromWindow !== false || levelPerfectFromWindow === undefined) && 
                                   (levelPerfectFromStorage !== false || levelPerfectFromStorage === undefined);
      
      console.debug(`🏆 [checkAllLevelsPerfect] Level ${i} combined perfect status: ${combinedPerfectStatus}`);
      
      if (!combinedPerfectStatus) {
        allPerfect = false;
        console.debug(`🏆 [checkAllLevelsPerfect] Found imperfect level: ${i}`);
        break;
      }
    }
    
    if (completedLevelCount === 0) {
      console.debug(`🏆 [checkAllLevelsPerfect] No completed levels found, can't be perfect`);
      return false;
    }
    
    const levelDivs = document.querySelectorAll('[data-level-perfect]');
    if (levelDivs.length > 0) {
      console.debug(`🏆 [checkAllLevelsPerfect] Found ${levelDivs.length} level divs with data-level-perfect attribute`);
      
      levelDivs.forEach(div => {
        const isPerfect = div.getAttribute('data-level-perfect') === 'true';
        console.debug(`🏆 [checkAllLevelsPerfect] Level perfect status from DOM: ${isPerfect}`);
        if (!isPerfect) {
          allPerfect = false;
        }
      });
    }
    
    console.debug(`🏆 [checkAllLevelsPerfect] All levels perfect: ${allPerfect}`);
    return allPerfect;
  };
  
  const updateLessonBonus = (isPerfect: boolean) => {
    const allLevelsPerfect = checkAllLevelsPerfect();
    
    const isPerfectFromState = isPerfectLesson;
    const isPerfectFromGlobal = (window as any).__PERFECT_LESSON__ === true;
    
    const wasAnyLevelSkipped = (window as any).__LEVEL_SKIPPED__ === true;
    
    let isPerfectFromStorage = true;
    let levelSkippedStorage = false;
    if (selectedLesson?.id) {
      try {
        isPerfectFromStorage = localStorage.getItem(`lesson_${selectedLesson.id}_perfect`) === 'true';
        levelSkippedStorage = localStorage.getItem(`lesson_${selectedLesson.id}_level_skipped`) === 'true';
      } catch (e) {
        console.error('Error reading localStorage:', e);
      }
    }
    
    let finalPerfectStatus = false;
    
    if (wasAnyLevelSkipped || levelSkippedStorage) {
      finalPerfectStatus = false;
      console.debug(`🏆 [updateLessonBonus] Level was skipped, lesson cannot be perfect`);
    } else {
      finalPerfectStatus = allLevelsPerfect && 
                          isPerfect && 
                          isPerfectFromState && 
                          isPerfectFromGlobal && 
                          isPerfectFromStorage;
    }
    
    console.debug(`🏆 [updateLessonBonus] Perfect status check:`);
    console.debug(`- All levels perfect: ${allLevelsPerfect}`);
    console.debug(`- Input isPerfect: ${isPerfect}`);
    console.debug(`- React state: ${isPerfectFromState}`);
    console.debug(`- Global flag: ${isPerfectFromGlobal}`);
    console.debug(`- Storage: ${isPerfectFromStorage}`);
    console.debug(`- Level skipped: ${wasAnyLevelSkipped || levelSkippedStorage}`);
    console.debug(`🏆 [updateLessonBonus] Final perfect status: ${finalPerfectStatus}`);
    
    if (isPerfectLesson !== finalPerfectStatus) {
      console.debug(`🏆 [updateLessonBonus] Updating React state to match final perfect status: ${finalPerfectStatus}`);
      setIsPerfectLesson(finalPerfectStatus);
      (window as any).__PERFECT_LESSON__ = finalPerfectStatus;
    }
    
    const bonus = calculateLessonBonus(finalPerfectStatus);
    console.debug(`🏆 [updateLessonBonus] Calculating lesson bonus: isPerfect=${finalPerfectStatus}, bonus=${bonus}`);
    setLessonBonus(bonus);
    return bonus;
  };

  const handleSelectLesson = useCallback((lesson: Lesson) => {
    console.log(`Selected lesson: ${lesson.name} with ${lesson.words.length} words`);
    
    resetLessonCheck();
    
    resetRecyclingCounts();
    
    setSelectedLesson(lesson);
    setTotalXpGained(0);
    setCurrentLevelIndex(0);
    setLevels([...LEVEL_CONFIGS]);
    
    setIsPerfectLesson(true);
    setLessonBonus(0);
    (window as any).__PERFECT_LESSON__ = true;
    
    (window as any).__LEVEL_SKIPPED__ = false;
    
    console.debug(`🏆 [handleSelectLesson] Starting new lesson with isPerfectLesson=true, levelSkipped=false`);
    
    try {
      localStorage.setItem(`lesson_${lesson.id}_perfect`, 'true');
      localStorage.setItem(`lesson_${lesson.id}_level_skipped`, 'false');
      console.debug(`🔐 Initialized lesson status in localStorage for lesson ${lesson.id}`);
    } catch (e) {
      console.error('Error setting localStorage:', e);
    }
    
    const firstLevelIndex = 0;
    const newLevels = [...LEVEL_CONFIGS];
    newLevels[firstLevelIndex].state = 'in-progress';
    setLevels(newLevels);
    setCurrentLevelIndex(firstLevelIndex);
    
    const generatedExercises = generateExercisesForLevel(newLevels[firstLevelIndex].type, lesson.words);
    console.log(`🚀 [handleSelectLesson] Generated ${generatedExercises.length} exercises`);
    
    setExercises(generatedExercises);
    setTotalOriginalExercises(generatedExercises.length);
    
    setCurrentExerciseIndex(0);
    setShowAnswer(false);
    setUserInput('');
    setIsInputCorrect(null);
    setWordsToReview([]);
    
    setShowLevelIntro(true);
    
    updateDebugInfo({
      lastAction: 'select-lesson',
      recyclingStatus: false,
      failedCards: []
    });
    
    setTimeout(() => {
      checkPreviouslyCompletedLevels();
    }, 500);
  }, [
    checkPreviouslyCompletedLevels, 
    resetLessonCheck, 
    setCurrentExerciseIndex, 
    setCurrentLevelIndex, 
    setExercises, 
    setIsInputCorrect, 
    setLevels, 
    setShowAnswer, 
    setShowLevelIntro, 
    setTotalOriginalExercises, 
    setUserInput, 
    setWordsToReview, 
    updateDebugInfo,
    resetRecyclingCounts
  ]);
  
  const handleBackToLessons = useCallback(() => {
    setSelectedLesson(null);
    setShowLevelIntro(false);
    setShowLevelComplete(false);
    setShowLessonComplete(false);
    setLevels([...LEVEL_CONFIGS]);
    updateDebugInfo({
      lastAction: 'back-to-lessons',
      recyclingStatus: false,
      failedCards: []
    });
  }, [setLevels, setSelectedLesson, setShowLessonComplete, setShowLevelComplete, setShowLevelIntro, updateDebugInfo]);
  
  const isLevelPreviouslyCompleted = useCallback((levelIndex: number) => {
    return previouslyCompletedLevels.includes(levelIndex);
  }, [previouslyCompletedLevels]);
  
  return {
    selectedLesson,
    currentExerciseIndex,
    showAnswer,
    userInput,
    isInputCorrect,
    exercises,
    totalXpGained,
    currentLevelIndex,
    levels,
    showLevelIntro,
    showLevelComplete,
    showLessonComplete,
    wordsToReview,
    correctlyAnsweredWords,
    
    currentExercise,
    progress,
    isLastExercise,
    currentLevel,
    totalExercises,
    isPerfectSpelling,
    isPerfectLevel,
    displayedExerciseNumber,
    previouslyCompletedLevels,
    levelCompletionCounts,
    isPracticeMode,
    isPerfectLesson,
    lessonBonus,
    
    setUserInput,
    handleSelectLesson,
    handleNextExercise,
    handlePrevExercise,
    handleCheckAnswer,
    handleBackToLessons,
    startLevel,
    completeLevel,
    moveToNextLevel,
    skipToNextLevel,
    restartCurrentLevel,
    isLevelPreviouslyCompleted,
    setShowLevelIntro,
    
    onLogActivity,
    
    handleImperfectLesson,
    updateLessonBonus,
    
    totalOriginalExercises,
    setWordsToReview
  };
};
